import { useState } from "react";
import styled from "@emotion/styled";
import {
  primaryFont,
  noOutlineOnFocus,
  fadeInAnimation,
  tooltipHeaderBackgroundColor,
} from "./styles";
import { rgba } from "@atlas-int/data";
import { text, surface, shadow, zIndex, borderRadius, spacing, fontSize, fontSizeValues, transition, dataViz, border as borderToken } from "@atlas-int/tokens";
import { Button as BaseButton } from "@atlas-int/ui";

const Root = styled.div`
  position: relative;
  font-family: ${primaryFont};
  z-index: ${zIndex.legendItem};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  text-align: left;
  margin: 0.1rem;
  text-transform: uppercase;
  font-size: clamp(0.6rem, 1vw, 1rem);
  font-family: ${primaryFont};
  background-color: ${surface.white};
  border: none;
  border-radius: ${borderRadius.base};
  transition: all ${transition.base};
  flex-shrink: 1;
  flex-grow: 0;
  flex-basis: 1;
  width: min-content;
  max-width: clamp(18rem, 20vw, 30rem);
  height: auto;
  min-height: 16px;
  white-space: nowrap;
  overflow: visible;
  padding: 0.15rem 0.5rem;
  cursor: pointer;
  ${noOutlineOnFocus}
`;

const Block = styled.div<{ $checked: boolean }>`
  width: clamp(0.5rem, 1vw, 1.5rem);
  height: clamp(0.5rem, 1vw, 1.5rem);
  margin-right: 0.2rem;
  position: relative;
  flex-shrink: 0;

  &:after {
    ${({ $checked }) => ($checked ? "content: '';" : "")}
    width: 0.1rem;
    border-radius: ${borderRadius.pill};
    height: 180%;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    transform: rotate(45deg);
    background-color: ${dataViz.textDark};
  }
`;

const HideIsolateRoot = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transform: translate(0, -100%);
  pointer-events: none;
  display: flex;
  justify-content: center;
  ${fadeInAnimation}
`;

const HideIsolateContent = styled.div`
  pointer-events: all;
  margin: auto;
  position: relative;
`;

const HideIsolateText = styled.div`
  position: relative;
  border-radius: ${borderRadius.base};
  padding: ${spacing[0]};
  margin-bottom: 11px;
  background-color: ${surface.white};
  color: ${text.primary};
  font-size: ${fontSize.xs};
  border: 1px solid ${borderToken.subtle};
  box-shadow: ${shadow.sm};

  &::before {
    content: "";
    position: absolute;
    top: 100%;
    left: calc(50% - 11px);
    width: 0;
    height: 0;
    border-left: 11px solid transparent;
    border-right: 11px solid transparent;
    border-top: 11px solid ${borderToken.subtle};
  }

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: calc(50% - 10px);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid ${surface.white};
  }
`;

const Title = styled.div`
  font-size: ${fontSize.base};
  text-align: center;
  white-space: nowrap;
  background-color: ${tooltipHeaderBackgroundColor};
  border-radius: ${borderRadius.base} ${borderRadius.base} 0px 0px;
  padding: ${spacing[2]} ${spacing[6]};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const HideIsolateButton = styled(BaseButton)`
  margin: ${spacing[4]};
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background-color: ${tooltipHeaderBackgroundColor};
  }
`;

export interface CategoryDatum {
  id: string;
  color: string;
  name: string | undefined;
}

interface Props {
  category: CategoryDatum;
  toggleCategory?: () => void;
  isolateCategory?: () => void;
  isHidden: boolean;
  isIsolated: boolean;
  smallFont?: boolean;
}

const Label = ({
  category: { color, name },
  toggleCategory,
  isolateCategory,
  isHidden,
  isIsolated,
  smallFont = false,
}: Props) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const toggleButton = toggleCategory ? (
    <HideIsolateButton variant="ghost" size="xs" onClick={toggleCategory}>
      Hide
    </HideIsolateButton>
  ) : null;
  const isolateButton = isolateCategory ? (
    <HideIsolateButton variant="ghost" size="xs" onClick={isolateCategory}>
      Keep Only
    </HideIsolateButton>
  ) : null;
  const hideIsolateButtons =
    isolateButton || toggleButton ? (
      <ButtonWrapper>
        {toggleButton}
        {isolateButton}
      </ButtonWrapper>
    ) : null;
  const hideIsolate = isHovered ? (
    <HideIsolateRoot>
      <HideIsolateContent>
        <HideIsolateText>
          <Title>{name}</Title>
          {hideIsolateButtons}
        </HideIsolateText>
      </HideIsolateContent>
    </HideIsolateRoot>
  ) : null;
  return (
    <Root
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ zIndex: isHovered ? zIndex.dropdown : undefined }}
    >
      <Button
        style={{
          backgroundColor: isHovered ? rgba(color, 0.3) : undefined,
          opacity: isHidden ? 0.5 : undefined,
          fontSize: smallFont ? fontSizeValues.xs : undefined,
        }}
        onClick={toggleCategory}
      >
        <Block style={{ backgroundColor: color }} $checked={isHidden} />
        {name}
      </Button>
      {hideIsolate}
    </Root>
  );
};

export default Label;
